import {useState} from 'react';
import {graphql, useFragment} from 'react-relay';
import styled from 'styled-components';
import {Button, EMPHASIS} from '@forecast-it/design-system';
import type {SettingsGeneralTab_account$key} from './__generated__/SettingsGeneralTab_account.graphql';
import {useSettingsContext} from './SettingsContext';
import {useUpdateAccount} from '../hooks/useUpdateAccount';

const AccountFragment = graphql`
	fragment SettingsGeneralTab_account on TicketingAccount {
		id
		autoCloseDays
		slug
		ticketNumberNext
		responseSlaValue
		responseSlaUnit
		resolutionSlaValue
		resolutionSlaUnit
	}
`;

const SLUG_RE = /^[A-Z][A-Z0-9]{1,9}$/;
const SLA_UNITS = ['hours', 'days'] as const;
type SlaUnit = (typeof SLA_UNITS)[number];

const Form = styled.form`
	max-width: 28rem;
	& > * + * {
		margin-top: 20px;
	}
`;

const BlockLabel = styled.label`
	display: block;
`;

const FieldLabelText = styled.span`
	font-size: 14px;
	line-height: 20px;
	font-weight: 500;
	color: #334155;
`;

const InlineRow = styled.div`
	margin-top: 4px;
	display: flex;
	align-items: center;
	gap: 8px;
`;

const NumberInput = styled.input`
	width: 6rem;
	border-radius: 4px;
	border: 1px solid #cbd5e1;
	padding: 8px 12px;
`;

const UnitText = styled.span`
	color: #475569;
`;

const SlugInput = styled.input`
	margin-top: 4px;
	width: 8rem;
	border-radius: 4px;
	border: 1px solid #cbd5e1;
	padding: 8px 12px;
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
	font-size: 14px;
	line-height: 20px;
	text-transform: uppercase;
`;

const PreviewText = styled.p`
	margin-top: 8px;
	font-size: 14px;
	line-height: 20px;
	color: #64748b;
`;

const MonoPreview = styled.span`
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
	color: #334155;
`;

const NextNumInput = styled.input`
	margin-top: 4px;
	width: 8rem;
	border-radius: 4px;
	border: 1px solid #cbd5e1;
	padding: 8px 12px;
`;

const WarningText = styled.p`
	margin-top: 4px;
	font-size: 12px;
	line-height: 16px;
	color: #b45309;
`;

const SectionFieldset = styled.fieldset`
	border-top: 1px solid #e2e8f0;
	padding-top: 20px;
	& > * + * {
		margin-top: 16px;
	}
`;

const SectionLegend = styled.legend`
	font-size: 14px;
	line-height: 20px;
	font-weight: 600;
	color: #0f172a;
`;

const SelectInput = styled.select`
	border-radius: 4px;
	border: 1px solid #cbd5e1;
	padding: 8px 12px;
`;

const HintText = styled.p`
	margin-top: 4px;
	font-size: 12px;
	line-height: 16px;
	color: #64748b;
`;

const AlertText = styled.p`
	font-size: 14px;
	line-height: 20px;
	color: #dc2626;
`;

const SavedText = styled.p`
	font-size: 14px;
	line-height: 20px;
	color: #15803d;
`;

export function SettingsGeneralTab() {
	const {account} = useSettingsContext();
	const data = useFragment<SettingsGeneralTab_account$key>(AccountFragment, account);
	const {update, loading: saving, error: mutationError, setError} = useUpdateAccount();

	// The unified schema types these as nullable; the backend always populates
	// them, so coalesce to safe defaults for the controlled-input drafts.
	const ticketNumberNext = data.ticketNumberNext ?? 1;
	const [autoCloseDraft, setAutoCloseDraft] = useState(String(data.autoCloseDays));
	const [slugDraft, setSlugDraft] = useState(data.slug ?? '');
	const [nextNumDraft, setNextNumDraft] = useState(String(ticketNumberNext));
	const [responseSlaValueDraft, setResponseSlaValueDraft] = useState(String(data.responseSlaValue));
	const [responseSlaUnitDraft, setResponseSlaUnitDraft] = useState<SlaUnit>(
		(SLA_UNITS as readonly string[]).includes(data.responseSlaUnit ?? '') ? (data.responseSlaUnit as SlaUnit) : 'hours'
	);
	const [resolutionSlaValueDraft, setResolutionSlaValueDraft] = useState(String(data.resolutionSlaValue));
	const [resolutionSlaUnitDraft, setResolutionSlaUnitDraft] = useState<SlaUnit>(
		(SLA_UNITS as readonly string[]).includes(data.resolutionSlaUnit ?? '') ? (data.resolutionSlaUnit as SlaUnit) : 'days'
	);
	const [saved, setSaved] = useState(false);
	const [validationError, setValidationError] = useState<string | null>(null);

	const isSaving = saving;

	async function handleSubmit() {
		setValidationError(null);
		setSaved(false);
		const n = Number(autoCloseDraft);
		if (!Number.isInteger(n) || autoCloseDraft.trim() === '' || isNaN(n)) {
			setValidationError('Must be a whole number');
			return;
		}
		if (n < 1 || n > 90) {
			setValidationError('Must be between 1 and 90');
			return;
		}
		const normalizedSlug = slugDraft.trim().toUpperCase();
		if (!SLUG_RE.test(normalizedSlug)) {
			setValidationError('Slug must be 2-10 letters or numbers and start with a letter');
			return;
		}
		const nextNum = Number(nextNumDraft);
		if (!Number.isInteger(nextNum) || nextNumDraft.trim() === '' || isNaN(nextNum)) {
			setValidationError('Next Ticket # must be a whole number');
			return;
		}
		if (nextNum < ticketNumberNext) {
			setValidationError(`Next Ticket # must be ≥ current value (${ticketNumberNext})`);
			return;
		}
		const responseSlaNum = Number(responseSlaValueDraft);
		if (!Number.isInteger(responseSlaNum) || responseSlaValueDraft.trim() === '' || responseSlaNum < 1) {
			setValidationError('Response SLA must be a positive whole number');
			return;
		}
		const resolutionSlaNum = Number(resolutionSlaValueDraft);
		if (!Number.isInteger(resolutionSlaNum) || resolutionSlaValueDraft.trim() === '' || resolutionSlaNum < 1) {
			setValidationError('Resolution SLA must be a positive whole number');
			return;
		}

		const accountInput: {
			autoCloseDays: number;
			slug: string;
			ticketNumberNext?: number;
			responseSlaValue: number;
			responseSlaUnit: SlaUnit;
			resolutionSlaValue: number;
			resolutionSlaUnit: SlaUnit;
		} = {
			autoCloseDays: n,
			slug: normalizedSlug,
			responseSlaValue: responseSlaNum,
			responseSlaUnit: responseSlaUnitDraft,
			resolutionSlaValue: resolutionSlaNum,
			resolutionSlaUnit: resolutionSlaUnitDraft,
		};
		if (nextNum !== ticketNumberNext) {
			accountInput.ticketNumberNext = nextNum;
		}
		try {
			await update(accountInput);
			setSaved(true);
		} catch {
			// error set by hook
		}
	}

	function handleFormSubmit(e: React.FormEvent) {
		e.preventDefault();
		void handleSubmit();
	}

	const displayError = validationError ?? mutationError;

	return (
		<Form noValidate onSubmit={handleFormSubmit}>
			<BlockLabel>
				<FieldLabelText>Auto-close resolved tickets after</FieldLabelText>
				<InlineRow>
					<NumberInput
						type="number"
						min={1}
						max={90}
						value={autoCloseDraft}
						onChange={e => {
							setAutoCloseDraft(e.target.value);
							setSaved(false);
							setValidationError(null);
							setError(null);
						}}
					/>
					<UnitText>days</UnitText>
				</InlineRow>
			</BlockLabel>
			<BlockLabel>
				<FieldLabelText>Ticket slug prefix</FieldLabelText>
				<SlugInput
					type="text"
					value={slugDraft}
					onChange={e => {
						setSlugDraft(e.target.value.toUpperCase());
						setSaved(false);
						setValidationError(null);
						setError(null);
					}}
				/>
				<PreviewText>
					Preview: <MonoPreview>{slugDraft || 'FOR'}-1042</MonoPreview>
				</PreviewText>
			</BlockLabel>
			<BlockLabel>
				<FieldLabelText>Next Ticket #</FieldLabelText>
				<NextNumInput
					type="number"
					min={ticketNumberNext}
					value={nextNumDraft}
					onChange={e => {
						setNextNumDraft(e.target.value);
						setSaved(false);
						setValidationError(null);
						setError(null);
					}}
				/>
				<WarningText>Warning: this cannot be undone. You cannot decrease this number once saved.</WarningText>
			</BlockLabel>

			<SectionFieldset>
				<SectionLegend>SLA Thresholds</SectionLegend>

				<BlockLabel>
					<FieldLabelText>Response SLA</FieldLabelText>
					<InlineRow>
						<NumberInput
							type="number"
							min={1}
							value={responseSlaValueDraft}
							onChange={e => {
								setResponseSlaValueDraft(e.target.value);
								setSaved(false);
								setValidationError(null);
								setError(null);
							}}
						/>
						<SelectInput
							value={responseSlaUnitDraft}
							onChange={e => {
								setResponseSlaUnitDraft(e.target.value as SlaUnit);
								setSaved(false);
								setValidationError(null);
								setError(null);
							}}
						>
							{SLA_UNITS.map(unit => (
								<option key={unit} value={unit}>
									{unit}
								</option>
							))}
						</SelectInput>
					</InlineRow>
					<HintText>Time to first response on a new ticket.</HintText>
				</BlockLabel>

				<BlockLabel>
					<FieldLabelText>Resolution SLA</FieldLabelText>
					<InlineRow>
						<NumberInput
							type="number"
							min={1}
							value={resolutionSlaValueDraft}
							onChange={e => {
								setResolutionSlaValueDraft(e.target.value);
								setSaved(false);
								setValidationError(null);
								setError(null);
							}}
						/>
						<SelectInput
							value={resolutionSlaUnitDraft}
							onChange={e => {
								setResolutionSlaUnitDraft(e.target.value as SlaUnit);
								setSaved(false);
								setValidationError(null);
								setError(null);
							}}
						>
							{SLA_UNITS.map(unit => (
								<option key={unit} value={unit}>
									{unit}
								</option>
							))}
						</SelectInput>
					</InlineRow>
					<HintText>Time from ticket open to resolution.</HintText>
				</BlockLabel>
			</SectionFieldset>

			{displayError && <AlertText role="alert">{displayError}</AlertText>}
			{saved && <SavedText>Saved.</SavedText>}
			<Button emphasis={EMPHASIS.HIGH} onPress={() => void handleSubmit()} disabled={isSaving}>
				{isSaving ? 'Saving…' : 'Save'}
			</Button>
		</Form>
	);
}
